import { AnimatePresence } from 'motion/react';
import type {
  ApiClient,
  IframePoolOrder,
  IframePoolOrderItem,
} from '../api';
import { ReadyStep } from './ReadyStep';
import { RevealBackgroundTint } from './RevealBackgroundTint';
import { RevealRays } from './RevealRays';
import { RevealGlowOverlay } from './RevealGlowOverlay';
import { BadgeRevealSequence } from './BadgeRevealSequence';
import { WinnerStep } from './WinnerStep';
import { useRevealMachine } from './useRevealMachine';

export interface PackRevealProps {
  client: ApiClient;
  orderId: string;
  offer: IframePoolOrder['poolOffer'];
  item: IframePoolOrderItem;
  onRevealed: (updated: IframePoolOrderItem) => void;
  onBuyAgain: () => void;
  onShowPack: () => void;
  /** "Confirm Shipping" → open the delivery flow for this item. */
  onShip?: (item: IframePoolOrderItem) => void;
  /** Fired after a successful buyback of this pack — the reveal screen
   *  advances to the next unsold pack, or closes once all are sold. */
  onSold: () => void;
  /** Re-load the order from the backend. Used to recover from a 422
   *  on `mark_as_opened` (item already opened — fresh payload has the
   *  authoritative `opened_at` + `best_active_offer`). */
  refetchOrder: () => Promise<IframePoolOrder | null>;
}

/**
 * State machine for a single pack:
 *
 *   ready   → slot wheel spins → call mark_as_opened → winner
 *   winner  → show item + FMV + Buyback (if offer exists) + Next
 *
 * If the item was already opened before we landed (F5 into reveal after
 * a partially‑completed session), start directly in `winner`.
 *
 * Everything stateful lives in `useRevealMachine`; this component is a
 * shell that wires hook output into JSX and forwards events.
 */
export function PackReveal({
  client,
  orderId,
  offer,
  item,
  onRevealed,
  onBuyAgain,
  onShowPack,
  onShip,
  onSold,
  refetchOrder,
}: PackRevealProps) {
  const machine = useRevealMachine({
    client,
    orderId,
    item,
    onRevealed,
    refetchOrder,
  });

  return (
    <>
      {/* Background tint — fades in after `mark_as_opened` returns
          a badge. Sits between the page gradient and the rays so
          the rays read on top. The base page background stays
          intact; we just wash a soft rarity-coloured halo over it. */}
      {machine.revealedBadge !== null && (
        <div className="pointer-events-none absolute inset-0 z-0">
          <RevealBackgroundTint badge={machine.revealedBadge} />
        </div>
      )}

      {/* Rotating rays — appear the moment the reveal phase starts,
          BEFORE the API answers. Always white; rarity is conveyed
          by the tint above + label inside the glow square. The
          `rotate` animation lives at PackReveal level so it doesn't
          reset on the ready→winner status swap. */}
      {machine.showRays && (
        <div className="pointer-events-none absolute inset-0 z-[1]">
          <RevealRays />
        </div>
      )}

      <AnimatePresence mode="wait">
        {machine.status === 'ready' && (
          <div key="ready" className="absolute inset-0 z-10">
            <ReadyStep
              offer={offer}
              onRevealStarted={machine.onRevealStarted}
              onExpansionStart={machine.onExpansionStart}
            />
          </div>
        )}
        {machine.status === 'winner' && (
          <div key="winner" className="absolute inset-0 z-10 overflow-x-hidden overflow-y-auto">
            <WinnerStep
              client={client}
              item={machine.current}
              offer={offer}
              sellState={machine.sellState}
              onSell={machine.onSell}
              onBuyAgain={onBuyAgain}
              onShowPack={onShowPack}
              {...(onShip ? { onShip } : {})}
              onSold={onSold}
              instant={machine.instant}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Glow overlay — single node owns BOTH the steady reveal-
          phase plate AND the scale-up wipe. Lives on PackReveal root
          (sibling of the AnimatePresence content) so it can bleed
          past the lane viewport's `overflow-hidden`. Phase machine:
          reveal (steady) → expand (scales up) → fade (opacity out). */}
      {machine.glowPhase !== null && (
        <RevealGlowOverlay
          phase={machine.glowPhase}
          badge={machine.revealedBadge}
          onExpandComplete={machine.onGlowExpanded}
          onFadeComplete={machine.onGlowFaded}
        />
      )}

      {/* Badge text sequence — character → grade. Lives above the
          glow (`z-40` vs glow's `z-30`) so the text reads on top of
          the expanded white pelt. `onComplete` releases the glow to
          fade, which then unmounts and reveals WinnerStep. */}
      {machine.showBadgeSequence && machine.current.badge && (
        <BadgeRevealSequence
          character={machine.current.badge.character}
          grade={machine.current.badge.grade}
          onComplete={machine.onBadgeSequenceDone}
        />
      )}
    </>
  );
}
